/** ResetDistributorPasswordsDialog — checklist-based password bulk reset.
 *
 * Flow:
 *   1. Fetch distributors list (GET /api/distributors)
 *   2. User selects via checkboxes (with Select All, Only Bulk-Created filters)
 *   3. Confirm action (danger copy)
 *   4. POST /api/distributors/bulk/reset-passwords-excel → Excel auto-downloads
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { WarningCircle, Key, DownloadSimple, CheckSquare, Square } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ResetDistributorPasswordsDialog({ open, onOpenChange }) {
  const [distributors, setDistributors] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [onlyBulkCreated, setOnlyBulkCreated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) { setSelected(new Set()); setSearch(''); setConfirming(false); setOnlyBulkCreated(false); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/distributors');
        setDistributors(r.data || []);
      } catch (e) {
        toast.error('Failed to load distributors');
      } finally { setLoading(false); }
    })();
  }, [open]);

  const visibleDistributors = distributors.filter(d => {
    if (onlyBulkCreated && !d.created_by_bulk) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.name || '').toLowerCase().includes(q) ||
           (d.email || '').toLowerCase().includes(q) ||
           (d.agency_names || []).join(',').toLowerCase().includes(q);
  });

  const toggle = (id) => {
    const ns = new Set(selected);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    setSelected(ns);
  };

  const toggleAllVisible = () => {
    const visIds = new Set(visibleDistributors.map(d => d.id));
    const allSelected = visibleDistributors.every(d => selected.has(d.id));
    const ns = new Set(selected);
    if (allSelected) {
      visIds.forEach(id => ns.delete(id));
    } else {
      visIds.forEach(id => ns.add(id));
    }
    setSelected(ns);
  };

  const allVisSelected = visibleDistributors.length > 0 && visibleDistributors.every(d => selected.has(d.id));

  const doReset = async () => {
    if (selected.size === 0) {
      toast.error('Kam se kam ek distributor select karo');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(
        '/distributors/bulk/reset-passwords-excel',
        { distributor_ids: Array.from(selected) },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `password_reset_${Date.now()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selected.size} distributor(s) ka password Patna@123 pe reset kar diya + Excel download ho gayi`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="reset-passwords-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key size={22} weight="duotone" className="text-purple-700" />
            Reset Distributor Passwords to Patna@123
          </DialogTitle>
          <div className="flex items-start gap-2 mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <WarningCircle size={16} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
            <div className="text-red-800">
              <b>Danger:</b> Selected distributors ka password <b>turant Patna@123</b> pe reset ho jayega.
              Ye operation undo nahi ho sakta — unko phir se wahi naya password dena hoga login ke liye.
              Reset hone wale distributors ki list Excel me auto-download hogi (audit ke liye).
            </div>
          </div>
        </DialogHeader>

        {!confirming ? (
          <div className="space-y-3">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <Input
                placeholder="Search name / email / agency…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 min-w-[220px]"
                data-testid="reset-pwd-search"
              />
              <label className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700">
                <input
                  type="checkbox"
                  checked={onlyBulkCreated}
                  onChange={(e) => setOnlyBulkCreated(e.target.checked)}
                  data-testid="reset-pwd-only-bulk"
                />
                Only bulk-created
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={toggleAllVisible}
                data-testid="reset-pwd-toggle-all"
              >
                {allVisSelected
                  ? <><Square size={14} /> Unselect All Visible</>
                  : <><CheckSquare size={14} /> Select All Visible ({visibleDistributors.length})</>}
              </Button>
            </div>

            {/* Summary bar */}
            <div className="bg-purple-50 border border-purple-200 rounded-md px-3 py-2 flex items-center justify-between text-xs">
              <span><b>{selected.size}</b> selected / <b>{distributors.length}</b> total</span>
              <span className="text-gray-500">{visibleDistributors.length} visible</span>
            </div>

            {/* Checklist */}
            <div className="border rounded-lg overflow-hidden max-h-[40vh] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
              ) : visibleDistributors.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">Koi distributor match nahi hua</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                    <tr>
                      <th className="px-2 py-2 w-10"></th>
                      <th className="px-2 py-2 text-left">Name</th>
                      <th className="px-2 py-2 text-left">Email</th>
                      <th className="px-2 py-2 text-left">Agencies</th>
                      <th className="px-2 py-2 text-left">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDistributors.map(d => (
                      <tr
                        key={d.id}
                        className={`border-t cursor-pointer ${selected.has(d.id) ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                        onClick={() => toggle(d.id)}
                        data-testid={`reset-pwd-row-${d.id}`}
                      >
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={selected.has(d.id)}
                            onChange={() => toggle(d.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`reset-pwd-check-${d.id}`}
                          />
                        </td>
                        <td className="px-2 py-2 font-medium text-gray-900 text-xs">{d.name}</td>
                        <td className="px-2 py-2 text-xs text-gray-600 font-mono">{d.email}</td>
                        <td className="px-2 py-2 text-xs text-gray-600 truncate max-w-[180px]" title={(d.agency_names || []).join(', ')}>
                          {(d.agency_names || []).join(', ') || '—'}
                        </td>
                        <td className="px-2 py-2 text-xs">
                          {d.created_by_bulk
                            ? <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">BULK</span>
                            : <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-semibold">MANUAL</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={() => setConfirming(true)}
                disabled={selected.size === 0}
                className="bg-red-600 hover:bg-red-700"
                data-testid="reset-pwd-proceed"
              >
                <Key size={14} weight="bold" className="mr-1" />
                Reset {selected.size} Password(s)
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4" data-testid="reset-pwd-confirm">
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 text-center space-y-2">
              <WarningCircle size={36} weight="duotone" className="text-red-600 mx-auto" />
              <div className="text-lg font-bold text-red-900">Sure ho?</div>
              <div className="text-sm text-red-800">
                <b>{selected.size}</b> distributor(s) ka password turant <code className="px-1.5 py-0.5 bg-red-100 rounded font-mono">Patna@123</code> pe reset ho jayega.
              </div>
              <div className="text-xs text-red-700 mt-2">
                Unko naya password (Patna@123) dena hoga login ke liye. Excel download ho jayega audit ke liye.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={loading}>
                Back
              </Button>
              <Button
                onClick={doReset}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="reset-pwd-confirm-btn"
              >
                <DownloadSimple size={14} weight="bold" className="mr-1" />
                {loading ? 'Resetting…' : 'Yes, Reset & Download Excel'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
